import { useState, useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtQty = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  wo: any;
  woLines: any[];
  sovLines: any[];
  onMutate: () => void;
}

/** Grid for PMs to mark individual WO line items as complete by clicking cells */
export const WOCompletionGrid = ({ wo, woLines, sovLines, onMutate }: Props) => {
  const [busy, setBusy] = useState<string | null>(null);

  const unitLines = woLines.filter((wol: any) => wol.unit_id);

  const woUnits = useMemo(() => {
    const unitMap = new Map<string, { id: string; number: string }>();
    unitLines.forEach((wol: any) => {
      if (wol.unit_id && wol.unit_schedule) {
        unitMap.set(wol.unit_id, { id: wol.unit_id, number: wol.unit_schedule.unit_number });
      }
    });
    return Array.from(unitMap.values()).sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  }, [unitLines]);

  const woSovLines = useMemo(() => {
    const slIds = new Set(unitLines.map((wol: any) => wol.sov_line_id));
    return (sovLines ?? []).filter((sl: any) => slIds.has(sl.id));
  }, [unitLines, sovLines]);

  const lineMap = useMemo(() => {
    const m = new Map<string, any>();
    unitLines.forEach((wol: any) => {
      m.set(`${wol.unit_id}::${wol.sov_line_id}`, wol);
    });
    return m;
  }, [unitLines]);

  const getLine = (unitId: string, sovLineId: string) => lineMap.get(`${unitId}::${sovLineId}`);

  const toggleComplete = async (wol: any) => {
    if (wol.status === "billed" || wol.status === "paid") return;
    setBusy(wol.id);
    try {
      const newStatus = wol.status === "complete" ? "pending" : "complete";
      const qty = Number(wol.quantity);
      const rate = Number(wol.contract_rate);

      const { error } = await supabase.from("work_order_lines").update({
        status: newStatus,
        completion_pct: newStatus === "complete" ? 100 : 0,
        completed_qty: newStatus === "complete" ? qty : 0,
        earned_amount: newStatus === "complete" ? qty * rate : 0,
      } as any).eq("id", wol.id);

      if (error) throw error;
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (woUnits.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No unit-level lines.</p>;
  }

  // Count stats
  const totalLines = unitLines.length;
  const completeCount = unitLines.filter((wol: any) => wol.status === "complete").length;
  const billedCount = unitLines.filter((wol: any) => wol.status === "billed").length;
  const paidCount = unitLines.filter((wol: any) => wol.status === "paid").length;

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium">{wo.number} — Mark Complete</span>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{completeCount} complete</span>
            <span>{billedCount} billed</span>
            <span>{paidCount} paid</span>
            <span>{totalLines - completeCount - billedCount - paidCount} pending</span>
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="text-amber-500">★</span> paid</span>
            <span className="flex items-center gap-1">●  billed</span>
            <span className="flex items-center gap-1"><span className="text-emerald-600">◕</span> complete</span>
            <span className="flex items-center gap-1"><span className="opacity-50">○</span> pending</span>
          </div>
        </div>

        <ScrollArea className="max-h-[400px]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 bg-background">
                <tr>
                  <th className="border p-1.5 text-left text-xs font-medium text-muted-foreground sticky left-0 bg-background min-w-[80px]">Unit</th>
                  {woSovLines.map((sl: any) => (
                    <th key={sl.id} className="border p-1.5 text-center text-xs font-medium min-w-[80px]">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <div className="font-mono text-[10px] text-muted-foreground">{sl.item_code ?? `#${sl.line_number}`}</div>
                            <div className="truncate max-w-[70px]">{sl.description?.split(" ")[0]}</div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent><p>{sl.description}</p></TooltipContent>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {woUnits.map(unit => (
                  <tr key={unit.id}>
                    <td className="border p-1.5 font-mono text-xs font-medium sticky left-0 bg-background">
                      {unit.number}
                    </td>
                    {woSovLines.map((sl: any) => {
                      const wol = getLine(unit.id, sl.id);
                      if (!wol) {
                        return <td key={sl.id} className="border p-1 text-center text-muted-foreground/30">—</td>;
                      }

                      const isPaid = wol.status === "paid";
                      const isBilled = wol.status === "billed";
                      const isComplete = wol.status === "complete";
                      const isProcessing = busy === wol.id;

                      if (isPaid) {
                        return (
                          <td key={sl.id} className="border p-1 text-center bg-amber-50 dark:bg-amber-950/30">
                            <span className="text-amber-500 text-xs">★</span>
                          </td>
                        );
                      }

                      if (isBilled) {
                        return (
                          <td key={sl.id} className="border p-1 text-center bg-muted/30">
                            <span className="text-muted-foreground text-xs">●</span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={sl.id}
                          className={cn(
                            "border p-1 text-center cursor-pointer transition-colors",
                            isComplete ? "bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-950/50" : "hover:bg-accent/30",
                            isProcessing && "opacity-50"
                          )}
                          onClick={() => !isProcessing && toggleComplete(wol)}
                        >
                          <span className={cn("text-xs", isComplete ? "text-emerald-600" : "text-muted-foreground/50")}>
                            {isComplete ? "◕" : "○"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollArea>
      </div>
    </TooltipProvider>
  );
};
